import { useCallback, useEffect, useMemo, useState } from "react";
import { storefrontProductTitle } from '../utils/storefrontProductTitle';
import { Navigate, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiBox,
  FiChevronRight,
  FiEdit3,
  FiGift,
  FiHeadphones,
  FiHeart,
  FiHome,
  FiMapPin,
  FiPackage,
  FiPlus,
  FiSave,
  FiSend,
  FiShoppingBag,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";

import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useWishlist } from "../context/WishlistContext";
import {productPath} from "../utils/productLinks";
import { apiRequest } from "../services/api";
import CustomerChangePassword from './common/CustomerChangePassword';

const inputClass =
  "h-12 w-full border-b border-slate-300 bg-transparent px-0 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-primary-800";

const emptyAddress = {
  label: "Home",
  recipient: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "IN",
  isDefault: false,
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

const titleCase = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getErrorMessage = (error) => {
  if (Array.isArray(error?.details?.message)) {
    return error.details.message.join(", ");
  }

  if (typeof error?.details?.message === "string") {
    return error.details.message;
  }

  return error?.message || "Something went wrong. Please try again.";
};

const getAssetUrl = (storageKey) => {
  if (!storageKey) return null;
  if (storageKey.startsWith("http")) return storageKey;

  const baseUrl = process.env.REACT_APP_ASSET_URL;

  return baseUrl
    ? `${baseUrl.replace(/\/$/, "")}/${storageKey.replace(/^\//, "")}`
    : null;
};

function StatusBadge({ value }) {
  const normalized = String(value || "").toUpperCase();

  const styles = {
    CONFIRMED: "border-blue-200 bg-blue-50 text-blue-700",
    PROCESSING: "border-amber-200 bg-amber-50 text-amber-700",
    SHIPPED: "border-violet-200 bg-violet-50 text-violet-700",
    DELIVERED: "border-emerald-200 bg-emerald-50 text-emerald-700",
    CANCELLED: "border-red-200 bg-red-50 text-red-700",
    OPEN: "border-blue-200 bg-blue-50 text-blue-700",
    IN_PROGRESS: "border-amber-200 bg-amber-50 text-amber-700",
    WAITING_FOR_CUSTOMER: "border-violet-200 bg-violet-50 text-violet-700",
    RESOLVED: "border-emerald-200 bg-emerald-50 text-emerald-700",
    CLOSED: "border-slate-200 bg-slate-100 text-slate-700",
  };

  return (
    <span
      className={`inline-flex border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${
        styles[normalized] || "border-slate-200 bg-slate-100 text-slate-700"
      }`}
    >
      {titleCase(value)}
    </span>
  );
}

function EmptyState({ icon, title, description, actionLabel, onAction }) {
  return (
    <div className="border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center bg-[#f7f2f0] text-primary-800">
        {icon}
      </div>

      <h3 className="mt-5 text-xl font-semibold tracking-tight text-slate-950">
        {title}
      </h3>

      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
        {description}
      </p>

      {actionLabel ? (
        <button
          type="button"
          onClick={onAction}
          className="mt-6 inline-flex items-center gap-2 bg-slate-950 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition hover:bg-primary-800"
        >
          {actionLabel}
          <FiArrowRight />
        </button>
      ) : null}
    </div>
  );
}

function SectionHeader({
  eyebrow = "My CHARMS Account",
  title,
  subtitle,
  action,
}) {
  return (
    <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary-800">
          {eyebrow}
        </p>

        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
          {title}
        </h2>

        {subtitle ? (
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            {subtitle}
          </p>
        ) : null}
      </div>

      {action}
    </div>
  );
}

function AccountStat({ label, value, icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group border border-slate-200 bg-white p-5 text-left transition hover:border-primary-800"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
            {label}
          </p>

          <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
            {value}
          </p>
        </div>

        <span className="flex h-10 w-10 items-center justify-center bg-[#f7f2f0] text-primary-800 transition group-hover:bg-primary-800 group-hover:text-white">
          {icon}
        </span>
      </div>
    </button>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();

  const {
    customer,
    accessToken,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useCustomerAuth();

  const [active, setActive] = useState("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const {
    items: wishlist,
    change: changeWishlist,
    refresh: refreshWishlist,
    loading: wishlistLoading,
    ready: wishlistReady,
  } = useWishlist();
  useEffect(() => {
    void refreshWishlist();
  }, [refreshWishlist]);
  const [loyalty, setLoyalty] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    fullName: "",
    phone: "",
    marketingOptIn: false,
  });

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressForm, setAddressForm] = useState(emptyAddress);

  const [ticketForm, setTicketForm] = useState({
    subject: "",
    category: "Order",
    message: "",
  });

  const [replyMessage, setReplyMessage] = useState("");

  const request = useCallback(
    (path, options = {}) =>
      apiRequest(path, {
        ...options,
        accessToken,
      }),
    [accessToken],
  );

  const loadAccount = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const [
        profileData,
        addressData,
        orderData,
        loyaltyData,
        transactionData,
        ticketData,
      ] = await Promise.all([
        request("/customer-account/profile"),
        request("/customer-account/addresses"),
        request("/customer-orders"),
        request("/customer-loyalty/summary"),
        request("/customer-loyalty/transactions"),
        request("/customer-support/tickets"),
      ]);

      setProfile(profileData);
      setAddresses(addressData || []);
      setOrders(orderData || []);
      setLoyalty(loyaltyData);
      setTransactions(transactionData || []);
      setTickets(ticketData || []);

      setProfileForm({
        fullName: profileData?.fullName || "",
        phone: profileData?.phone || "",
        marketingOptIn: Boolean(profileData?.marketingOptIn),
      });
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, request]);

  useEffect(() => {
    if (isAuthenticated && accessToken) {
      loadAccount();
    }
  }, [isAuthenticated, accessToken, loadAccount]);

  const runAction = async (action, successMessage) => {
    try {
      setIsSaving(true);
      setError("");
      setNotice("");

      await action();

      if (successMessage) {
        setNotice(successMessage);
      }

      await loadAccount();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  const saveProfile = (event) => {
    event.preventDefault();

    runAction(async () => {
      const updatedProfile = await request("/customer-account/profile", {
        method: "PATCH",
        body: profileForm,
      });

      setProfile(updatedProfile);
      setEditingProfile(false);
    }, "Your profile has been updated.");
  };

  const openCreateAddress = () => {
    setEditingAddressId(null);
    setAddressForm({
      ...emptyAddress,
      isDefault: addresses.length === 0,
    });
    setShowAddressForm(true);
  };

  const openEditAddress = (address) => {
    setEditingAddressId(address.id);
    setAddressForm({
      label: address.label || "Home",
      recipient: address.recipient || "",
      phone: address.phone || "",
      addressLine1: address.addressLine1 || "",
      addressLine2: address.addressLine2 || "",
      city: address.city || "",
      state: address.state || "",
      postalCode: address.postalCode || "",
      country: address.country || "IN",
      isDefault: Boolean(address.isDefault),
    });
    setShowAddressForm(true);
  };

  const saveAddress = (event) => {
    event.preventDefault();

    runAction(
      async () => {
        const path = editingAddressId
          ? `/customer-account/addresses/${editingAddressId}`
          : "/customer-account/addresses";

        await request(path, {
          method: editingAddressId ? "PATCH" : "POST",
          body: addressForm,
        });

        setShowAddressForm(false);
        setEditingAddressId(null);
        setAddressForm(emptyAddress);
      },
      editingAddressId ? "Address updated." : "Address added.",
    );
  };

  const removeAddress = (addressId) => {
    if (!window.confirm("Remove this saved address?")) return;

    runAction(
      () =>
        request(`/customer-account/addresses/${addressId}`, {
          method: "DELETE",
        }),
      "Address removed.",
    );
  };

  const removeWishlistItem = async (variantId) => {
    if (isSaving) return;
    setIsSaving(true);
    setNotice("");
    setError("");
    try {
      if (await changeWishlist(variantId, false))
        setNotice("Removed from wishlist.");
      else setError("Wishlist could not be updated. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const cancelOrder = (orderId) => {
    if (!window.confirm("Cancel this order?")) return;

    runAction(
      () =>
        request(`/customer-orders/${orderId}/cancel`, {
          method: "POST",
        }),
      "Order cancelled.",
    );
  };

  const loadTicket = async (ticketId) => {
    try {
      setError("");
      setSelectedTicket(await request(`/customer-support/tickets/${ticketId}`));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    }
  };

  const createTicket = (event) => {
    event.preventDefault();

    runAction(async () => {
      const ticket = await request("/customer-support/tickets", {
        method: "POST",
        body: ticketForm,
      });

      setTicketForm({
        subject: "",
        category: "Order",
        message: "",
      });

      setSelectedTicket(ticket);
    }, "Support ticket created.");
  };

  const sendReply = (event) => {
    event.preventDefault();

    if (!selectedTicket?.id || !replyMessage.trim()) return;

    runAction(async () => {
      const ticket = await request(
        `/customer-support/tickets/${selectedTicket.id}/replies`,
        {
          method: "POST",
          body: { message: replyMessage },
        },
      );

      setSelectedTicket(ticket);
      setReplyMessage("");
    }, "Reply sent.");
  };

  const closeTicket = () => {
    if (!selectedTicket?.id) return;

    runAction(async () => {
      await request(`/customer-support/tickets/${selectedTicket.id}/close`, {
        method: "POST",
      });

      setSelectedTicket(null);
    }, "Support ticket closed.");
  };

  const recentOrders = useMemo(() => orders.slice(0, 3), [orders]);

  if (isAuthLoading || (isAuthenticated && isLoading)) {
    return (
      <div className="min-h-screen bg-[#faf9f7] px-4 py-10">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[240px_1fr]">
          <div className="h-[460px] animate-pulse bg-slate-200" />
          <div className="space-y-6">
            <div className="h-56 animate-pulse bg-[#eadde1]" />
            <div className="grid gap-4 sm:grid-cols-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-32 animate-pulse bg-white" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !customer) {
    return <Navigate to="/signin" replace />;
  }

  const account = profile || customer;
  const initials =
    account.fullName
      ?.split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((name) => name.charAt(0).toUpperCase())
      .join("") || "C";

  const menuItems = [
    { key: "overview", label: "Overview", icon: <FiHome /> },
    { key: "orders", label: "Orders", icon: <FiShoppingBag /> },
    { key: "rewards", label: "Rewards", icon: <FiGift /> },
    { key: "wishlist", label: "Wishlist", icon: <FiHeart /> },
    { key: "addresses", label: "Addresses", icon: <FiMapPin /> },
    { key: "profile", label: "Profile", icon: <FiUser /> },
    { key: "support", label: "Support", icon: <FiHeadphones /> },
  ];

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-12">
        <div className="border-b border-slate-200 pb-7">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary-800">
            CHARMS
          </p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-slate-950">
            My Account
          </h1>
        </div>

        {error ? (
          <div className="mt-6 border border-red-200 bg-red-50 px-5 py-4 text-sm font-medium text-red-700">
            {error}
          </div>
        ) : null}

        {notice ? (
          <div className="mt-6 border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-700">
            {notice}
          </div>
        ) : null}

        <div className="mt-8 grid gap-8 lg:grid-cols-[240px_1fr]">
          <aside>
            <div className="border border-slate-200 bg-white p-5 lg:sticky lg:top-28">
              <div className="border-b border-slate-200 pb-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center bg-slate-950 text-sm font-bold text-white">
                    {initials}
                  </div>

                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-950">
                      {account.fullName}
                    </p>
                    <p className="mt-1 truncate text-xs text-slate-500">
                      {account.email || account.phone || "CHARMS Customer"}
                    </p>
                  </div>
                </div>
              </div>

              <nav className="mt-4 space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setActive(item.key)}
                    className={`flex w-full items-center justify-between px-3 py-3 text-left text-sm font-semibold transition ${
                      active === item.key
                        ? "bg-primary-800 text-white"
                        : "text-slate-700 hover:bg-[#faf7f5] hover:text-primary-800"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      {item.icon}
                      {item.label}
                    </span>
                    <FiChevronRight size={16} />
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          <main className="min-w-0">
            {active === "overview" ? (
              <div className="space-y-8">
                <section className="bg-slate-950 px-6 py-10 text-white sm:px-10">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                    CHARMS Membership
                  </p>

                  <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                    Welcome back, {account.fullName?.split(" ")[0] || "there"}.
                  </h2>

                  <p className="mt-3 max-w-xl text-sm leading-6 text-white/70">
                    Everything you need for your CHARMS orders, rewards,
                    addresses and saved pieces.
                  </p>

                  <button
                    type="button"
                    onClick={() => navigate("/search/all")}
                    className="mt-7 inline-flex items-center gap-2 border border-white bg-white px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-950 transition hover:border-primary-800 hover:bg-primary-800 hover:text-white"
                  >
                    Continue Shopping
                    <FiArrowRight />
                  </button>
                </section>

                <section className="grid gap-4 sm:grid-cols-3">
                  <AccountStat
                    label="Orders"
                    value={orders.length}
                    icon={<FiShoppingBag size={20} />}
                    onClick={() => setActive("orders")}
                  />
                  <AccountStat
                    label="Reward Points"
                    value={loyalty?.account?.pointsBalance || 0}
                    icon={<FiGift size={20} />}
                    onClick={() => setActive("rewards")}
                  />
                  <AccountStat
                    label="Addresses"
                    value={addresses.length}
                    icon={<FiMapPin size={20} />}
                    onClick={() => setActive("addresses")}
                  />
                </section>

                <section className="border border-slate-200 bg-white p-6 sm:p-8">
                  <div className="flex items-end justify-between gap-4 border-b border-slate-200 pb-5">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-800">
                        Order History
                      </p>
                      <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                        Recent Orders
                      </h2>
                    </div>

                    {orders.length ? (
                      <button
                        type="button"
                        onClick={() => setActive("orders")}
                        className="text-xs font-bold uppercase tracking-[0.12em] text-primary-800"
                      >
                        View all
                      </button>
                    ) : null}
                  </div>

                  <div className="mt-6">
                    {!recentOrders.length ? (
                      <EmptyState
                        title="No orders yet"
                        description="Your confirmed CHARMS orders will appear here."
                        icon={<FiPackage size={25} />}
                        actionLabel="Shop Now"
                        onAction={() => navigate("/search/all")}
                      />
                    ) : (
                      <div className="divide-y divide-slate-200">
                        {recentOrders.map((order) => (
                          <article
                            key={order.id}
                            className="flex flex-col gap-4 py-5 first:pt-0 sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex h-11 w-11 items-center justify-center bg-[#f7f2f0] text-primary-800">
                                <FiBox size={19} />
                              </div>

                              <div>
                                <p className="font-semibold text-slate-950">
                                  {order.orderNumber}
                                </p>
                                <p className="mt-1 text-sm text-slate-500">
                                  Placed {formatDate(order.createdAt)}
                                </p>
                              </div>
                            </div>

                            <div className="sm:text-right">
                              <p className="font-semibold text-slate-950">
                                {formatCurrency(order.totalAmount)}
                              </p>
                              <div className="mt-2">
                                <StatusBadge value={order.status} />
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            ) : null}

            {active === "orders" ? (
              <section className="space-y-6">
                <SectionHeader
                  title="My Orders"
                  subtitle="Track ecommerce orders placed with your CHARMS account."
                />

                {!orders.length ? (
                  <EmptyState
                    title="You have not placed an order yet"
                    description="Your confirmed ecommerce orders will appear here."
                    icon={<FiShoppingBag size={25} />}
                    actionLabel="Browse Products"
                    onAction={() => navigate("/search/all")}
                  />
                ) : (
                  <div className="divide-y border border-slate-200 bg-white">
                    {orders.map((order) => (
                      <article
                        key={order.id}
                        className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
                      >
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-primary-800">
                            {order.orderNumber}
                          </p>
                          <h3 className="mt-2 font-semibold text-slate-950">
                            {order._count?.items || 0} item
                            {(order._count?.items || 0) !== 1 ? "s" : ""}
                          </h3>
                          <p className="mt-1 text-sm text-slate-500">
                            Placed {formatDate(order.createdAt)}
                          </p>
                        </div>

                        <div className="sm:text-right">
                          <p className="text-lg font-semibold text-slate-950">
                            {formatCurrency(order.totalAmount)}
                          </p>
                          <div className="mt-2">
                            <StatusBadge value={order.status} />
                          </div>

                          {["CONFIRMED", "PENDING_PAYMENT"].includes(
                            order.status,
                          ) ? (
                            <button
                              type="button"
                              onClick={() => cancelOrder(order.id)}
                              disabled={isSaving}
                              className="mt-4 text-xs font-bold uppercase tracking-[0.12em] text-red-700 hover:text-red-900 disabled:opacity-50"
                            >
                              Cancel order
                            </button>
                          ) : null}
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            {active === "rewards" ? (
              <section className="space-y-6">
                <SectionHeader
                  title="Rewards"
                  subtitle="Your loyalty balance and reward activity."
                />

                <div className="grid gap-px bg-slate-200 sm:grid-cols-3">
                  <div className="bg-slate-950 p-7 text-white sm:col-span-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
                      Available Points
                    </p>
                    <p className="mt-4 text-5xl font-semibold tracking-tight">
                      {loyalty?.account?.pointsBalance || 0}
                    </p>
                    <p className="mt-3 text-sm text-white/65">
                      Redeem during checkout.
                    </p>
                  </div>

                  <div className="bg-white p-7">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                      Lifetime Earned
                    </p>
                    <p className="mt-4 text-3xl font-semibold text-slate-950">
                      {loyalty?.account?.lifetimeEarned || 0}
                    </p>
                  </div>

                  <div className="bg-white p-7">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                      Lifetime Redeemed
                    </p>
                    <p className="mt-4 text-3xl font-semibold text-slate-950">
                      {loyalty?.account?.lifetimeRedeemed || 0}
                    </p>
                  </div>
                </div>

                <div className="border border-slate-200 bg-white p-6 sm:p-8">
                  <h3 className="text-xl font-semibold tracking-tight text-slate-950">
                    Reward History
                  </h3>

                  {!transactions.length ? (
                    <p className="py-10 text-center text-sm text-slate-500">
                      No reward transactions yet.
                    </p>
                  ) : (
                    <div className="mt-5 divide-y divide-slate-200">
                      {transactions.map((transaction) => (
                        <div
                          key={transaction.id}
                          className="flex items-center justify-between gap-4 py-4"
                        >
                          <div>
                            <p className="font-semibold text-slate-950">
                              {transaction.note || titleCase(transaction.type)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {formatDate(transaction.createdAt)}
                            </p>
                          </div>

                          <div className="text-right">
                            <p
                              className={`font-semibold ${
                                Number(transaction.points) >= 0
                                  ? "text-emerald-700"
                                  : "text-red-700"
                              }`}
                            >
                              {Number(transaction.points) >= 0 ? "+" : ""}
                              {transaction.points} points
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Balance: {transaction.balanceAfter}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            ) : null}

            {active === "wishlist" ? (
              <section className="space-y-6">
                <SectionHeader
                  title="Wishlist"
                  subtitle="Pieces you have saved for later."
                />

                {wishlistLoading && !wishlist.length ? (
                  <p className="py-8 text-center text-slate-500">
                    Loading wishlist…
                  </p>
                ) : !wishlistReady ? (
                  <div className="py-8 text-center text-slate-500">
                    <p>Unable to load your saved wishlist.</p>
                    <button
                      onClick={refreshWishlist}
                      className="mt-3 font-semibold text-primary-800 underline"
                    >
                      Retry
                    </button>
                  </div>
                ) : !wishlist.length ? (
                  <EmptyState
                    title="Your wishlist is empty"
                    description="Save products you love and return to them anytime."
                    icon={<FiHeart size={25} />}
                    actionLabel="Explore Products"
                    onAction={() => navigate("/search/all")}
                  />
                ) : (
                  <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                    {wishlist.map((item) => {
                      const variant = item.variant;
                      const product = variant?.product;
                      const imageUrl = getAssetUrl(
                        product?.images?.[0]?.storageKey,
                      );

                      return (
                        <article
                          key={item.id}
                          className="group overflow-hidden border border-slate-200 bg-white"
                        >
                          <button
                            type="button"
                            onClick={() => navigate(productPath(product))}
                            className="block h-72 w-full overflow-hidden bg-slate-100"
                          >
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={product?.name || "Product"}
                                className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                              />
                            ) : (
                              <span className="flex h-full items-center justify-center text-slate-400">
                                <FiPackage size={30} />
                              </span>
                            )}
                          </button>

                          <div className="p-5">
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-primary-800">
                              {product?.brand?.name || "CHARMS"}
                            </p>
                            <h3 className="mt-2 font-semibold text-slate-950">
                              {storefrontProductTitle(product)}
                            </h3>
                            {product.productCode && <p className="mt-1 text-xs text-slate-500">Article No: {product.productCode}</p>}
                            <p className="mt-1 text-sm text-slate-500">
                              {[variant?.color?.name, variant?.size?.name]
                                .filter(Boolean)
                                .join(" · ") || variant?.variantName}
                            </p>
                            <p className="mt-4 text-lg font-semibold text-slate-950">
                              {Number(variant?.onlinePrice) > 0 ? formatCurrency(variant.onlinePrice) : "Currently unavailable"}
                            </p>

                            <div className="mt-5 flex gap-3">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(productPath(product))
                                }
                                className="flex-1 bg-slate-950 py-3 text-xs font-bold uppercase tracking-[0.1em] text-white transition hover:bg-primary-800"
                              >
                                View Product
                              </button>

                              <button
                                type="button"
                                onClick={() => removeWishlistItem(variant.id)}
                                className="flex h-11 w-11 items-center justify-center border border-red-200 text-red-700 transition hover:bg-red-50"
                                aria-label="Remove from wishlist"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            ) : null}

            {active === "addresses" ? (
              <section className="space-y-6">
                <SectionHeader
                  title="Saved Addresses"
                  subtitle="Manage delivery addresses for checkout."
                  action={
                    <button
                      type="button"
                      onClick={openCreateAddress}
                      className="inline-flex items-center gap-2 bg-slate-950 px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition hover:bg-primary-800"
                    >
                      <FiPlus />
                      Add Address
                    </button>
                  }
                />

                {showAddressForm ? (
                  <form
                    onSubmit={saveAddress}
                    className="border border-primary-200 bg-white p-6 sm:p-8"
                  >
                    <div className="flex items-center justify-between border-b border-slate-200 pb-5">
                      <h3 className="text-xl font-semibold text-slate-950">
                        {editingAddressId ? "Edit Address" : "New Address"}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setShowAddressForm(false)}
                        className="text-slate-500 hover:text-slate-950"
                      >
                        <FiX size={22} />
                      </button>
                    </div>

                    <div className="mt-6 grid gap-5 sm:grid-cols-2">
                      {[
                        ["label", "Label", "Home"],
                        ["recipient", "Recipient Name", "Full name"],
                        ["phone", "Phone Number", "9876543210"],
                        ["addressLine1", "Address Line 1", "House / Street"],
                        ["addressLine2", "Address Line 2", "Optional"],
                        ["city", "City", "City"],
                        ["state", "State", "State"],
                        ["postalCode", "Postal Code", "141001"],
                      ].map(([name, label, placeholder]) => (
                        <label
                          key={name}
                          className={
                            name === "addressLine1" || name === "addressLine2"
                              ? "sm:col-span-2"
                              : ""
                          }
                        >
                          <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                            {label}
                          </span>
                          <input
                            required={!["label", "addressLine2"].includes(name)}
                            name={name}
                            placeholder={placeholder}
                            value={addressForm[name]}
                            onChange={(event) =>
                              setAddressForm((current) => ({
                                ...current,
                                [name]: event.target.value,
                              }))
                            }
                            className={`mt-2 ${inputClass}`}
                          />
                        </label>
                      ))}
                    </div>

                    <label className="mt-6 flex items-center gap-3 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={addressForm.isDefault}
                        onChange={(event) =>
                          setAddressForm((current) => ({
                            ...current,
                            isDefault: event.target.checked,
                          }))
                        }
                        className="h-4 w-4 accent-primary-800"
                      />
                      Make this my default address
                    </label>

                    <button
                      disabled={isSaving}
                      className="mt-7 inline-flex items-center gap-2 bg-primary-800 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white disabled:opacity-60"
                    >
                      <FiSave />
                      {isSaving ? "Saving..." : "Save Address"}
                    </button>
                  </form>
                ) : null}

                {!addresses.length ? (
                  <EmptyState
                    title="No saved address"
                    description="Add a delivery address before placing your next order."
                    icon={<FiMapPin size={25} />}
                    actionLabel="Add Address"
                    onAction={openCreateAddress}
                  />
                ) : (
                  <div className="grid gap-5 md:grid-cols-2">
                    {addresses.map((address) => (
                      <article
                        key={address.id}
                        className="border border-slate-200 bg-white p-6"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-950">
                                {address.label || "Address"}
                              </h3>
                              {address.isDefault ? (
                                <span className="border border-primary-200 bg-primary-50 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] text-primary-800">
                                  Default
                                </span>
                              ) : null}
                            </div>

                            <p className="mt-5 font-medium text-slate-950">
                              {address.recipient}
                            </p>

                            <p className="mt-2 text-sm leading-6 text-slate-500">
                              {address.addressLine1}
                              {address.addressLine2
                                ? `, ${address.addressLine2}`
                                : ""}
                              <br />
                              {address.city}, {address.state}{" "}
                              {address.postalCode}
                              <br />
                              {address.phone}
                            </p>
                          </div>
                        </div>

                        <div className="mt-6 flex gap-5 border-t border-slate-200 pt-4">
                          <button
                            type="button"
                            onClick={() => openEditAddress(address)}
                            className="text-xs font-bold uppercase tracking-[0.12em] text-primary-800"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeAddress(address.id)}
                            className="text-xs font-bold uppercase tracking-[0.12em] text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            {active === "profile" ? (
              <section className="space-y-6">
                <CustomerChangePassword />
                <SectionHeader
                  title="Profile Information"
                  subtitle="Keep your contact details up to date."
                  action={
                    <button
                      type="button"
                      onClick={() => setEditingProfile((current) => !current)}
                      className="inline-flex items-center gap-2 border border-slate-300 px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-700 transition hover:border-primary-800 hover:text-primary-800"
                    >
                      <FiEdit3 />
                      {editingProfile ? "Cancel" : "Edit Profile"}
                    </button>
                  }
                />

                <div className="border border-slate-200 bg-white p-6 sm:p-8">
                  {editingProfile ? (
                    <form onSubmit={saveProfile} className="max-w-xl space-y-6">
                      <label>
                        <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                          Full Name
                        </span>
                        <input
                          required
                          value={profileForm.fullName}
                          onChange={(event) =>
                            setProfileForm((current) => ({
                              ...current,
                              fullName: event.target.value,
                            }))
                          }
                          className={`mt-2 ${inputClass}`}
                        />
                      </label>

                      <label>
                        <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                          Mobile Number (with country code)
                        </span>
                        <input
                          value={profileForm.phone}
                          onChange={(event) =>
                            setProfileForm((current) => ({
                              ...current,
                              phone: event.target.value,
                            }))
                          }
                          className={`mt-2 ${inputClass}`}
                        />
                      </label>

                      <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                        <input
                          type="checkbox"
                          checked={profileForm.marketingOptIn}
                          onChange={(event) =>
                            setProfileForm((current) => ({
                              ...current,
                              marketingOptIn: event.target.checked,
                            }))
                          }
                          className="h-4 w-4 accent-primary-800"
                        />
                        Send me new collections and CHARMS offers.
                      </label>

                      <button
                        disabled={isSaving}
                        className="inline-flex items-center gap-2 bg-primary-800 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white disabled:opacity-60"
                      >
                        <FiSave />
                        {isSaving ? "Saving..." : "Save Changes"}
                      </button>
                    </form>
                  ) : (
                    <div className="grid gap-7 sm:grid-cols-2">
                      {[
                        ["Full Name", account.fullName],
                        ["Email Address", account.email || "Not added"],
                        ["Mobile Number", account.phone || "Not added"],
                        [
                          "Account Status",
                          titleCase(account.status || "ACTIVE"),
                        ],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                            {label}
                          </p>
                          <p className="mt-2 font-semibold text-slate-950">
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            ) : null}

            {active === "support" ? (
              <section className="space-y-6">
                <SectionHeader
                  title="Support"
                  subtitle="Create a ticket and communicate directly with the CHARMS support team."
                />

                <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
                  <form
                    onSubmit={createTicket}
                    className="border border-slate-200 bg-white p-6"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-primary-800">
                      Need Help?
                    </p>
                    <h3 className="mt-2 text-xl font-semibold text-slate-950">
                      Create a Ticket
                    </h3>

                    <div className="mt-6 space-y-5">
                      <input
                        required
                        placeholder="Subject"
                        value={ticketForm.subject}
                        onChange={(event) =>
                          setTicketForm((current) => ({
                            ...current,
                            subject: event.target.value,
                          }))
                        }
                        className={inputClass}
                      />

                      <select
                        value={ticketForm.category}
                        onChange={(event) =>
                          setTicketForm((current) => ({
                            ...current,
                            category: event.target.value,
                          }))
                        }
                        className={inputClass}
                      >
                        <option value="Order">Order</option>
                        <option value="Delivery">Delivery</option>
                        <option value="Product">Product</option>
                        <option value="Payment">Payment</option>
                        <option value="Other">Other</option>
                      </select>

                      <textarea
                        required
                        rows="6"
                        placeholder="How can we help?"
                        value={ticketForm.message}
                        onChange={(event) =>
                          setTicketForm((current) => ({
                            ...current,
                            message: event.target.value,
                          }))
                        }
                        className="w-full border-b border-slate-300 bg-transparent px-0 py-3 text-sm text-slate-950 outline-none placeholder:text-slate-400 focus:border-primary-800"
                      />
                    </div>

                    <button
                      disabled={isSaving}
                      className="mt-7 inline-flex items-center gap-2 bg-primary-800 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white disabled:opacity-60"
                    >
                      <FiSend />
                      Create Ticket
                    </button>
                  </form>

                  <div>
                    {selectedTicket ? (
                      <div className="border border-slate-200 bg-white p-6">
                        <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-5">
                          <div>
                            <button
                              type="button"
                              onClick={() => setSelectedTicket(null)}
                              className="text-xs font-bold uppercase tracking-[0.12em] text-primary-800"
                            >
                              ← All Tickets
                            </button>
                            <h3 className="mt-3 text-lg font-semibold text-slate-950">
                              {selectedTicket.subject}
                            </h3>
                            <p className="mt-1 text-xs text-slate-500">
                              {selectedTicket.ticketNumber}
                            </p>
                          </div>
                          <StatusBadge value={selectedTicket.status} />
                        </div>

                        <div className="mt-6 max-h-80 space-y-3 overflow-y-auto">
                          {selectedTicket.messages?.map((message) => (
                            <div
                              key={message.id}
                              className={`p-4 text-sm leading-6 ${
                                message.sender === "CUSTOMER"
                                  ? "ml-8 bg-[#f7f2f0] text-primary-900"
                                  : "mr-8 bg-slate-100 text-slate-700"
                              }`}
                            >
                              <p>{message.message}</p>
                              <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.1em] opacity-60">
                                {message.sender} ·{" "}
                                {formatDate(message.createdAt)}
                              </p>
                            </div>
                          ))}
                        </div>

                        {!["CLOSED", "RESOLVED"].includes(
                          selectedTicket.status,
                        ) ? (
                          <form onSubmit={sendReply} className="mt-6">
                            <textarea
                              required
                              rows="3"
                              placeholder="Write a reply..."
                              value={replyMessage}
                              onChange={(event) =>
                                setReplyMessage(event.target.value)
                              }
                              className="w-full border border-slate-300 p-3 text-sm outline-none focus:border-primary-800"
                            />

                            <div className="mt-3 flex flex-wrap gap-3">
                              <button className="inline-flex items-center gap-2 bg-primary-800 px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white">
                                <FiSend />
                                Send Reply
                              </button>

                              <button
                                type="button"
                                onClick={closeTicket}
                                className="border border-slate-300 px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-700"
                              >
                                Close Ticket
                              </button>
                            </div>
                          </form>
                        ) : null}
                      </div>
                    ) : !tickets.length ? (
                      <EmptyState
                        title="No support tickets"
                        description="Create a ticket whenever you need help from CHARMS."
                        icon={<FiHeadphones size={25} />}
                      />
                    ) : (
                      <div className="divide-y border border-slate-200 bg-white">
                        {tickets.map((ticket) => (
                          <button
                            key={ticket.id}
                            type="button"
                            onClick={() => loadTicket(ticket.id)}
                            className="w-full p-5 text-left transition hover:bg-[#faf7f5]"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-primary-800">
                                  {ticket.ticketNumber}
                                </p>
                                <h3 className="mt-2 font-semibold text-slate-950">
                                  {ticket.subject}
                                </h3>
                                <p className="mt-2 text-xs text-slate-500">
                                  Updated {formatDate(ticket.lastMessageAt)}
                                </p>
                              </div>
                              <StatusBadge value={ticket.status} />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
